import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	FlaskConical,
	Play,
	CheckCircle2,
	ClipboardCheck,
	DollarSign,
	Trash2,
	RefreshCw,
	AlertTriangle,
	Users,
	Calendar,
	Activity,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import * as api from "@/services/api";
import type {
	TestLabBatch,
	TestLabPreset,
	TestLabUserVerification,
	TestLabPayrollPreview,
} from "@/types";

/** Display org-TZ dates (backend now returns yyyy-MM-dd strings in org timezone). */
function formatOrgDate(dateStr: string): string {
	// Handle yyyy-MM-dd format directly to avoid timezone shifts
	const match = dateStr?.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (match) {
		return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
	}
	// Fallback for legacy UTC date strings
	const d = new Date(dateStr);
	return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

const statusColors: Record<string, string> = {
	generating: "bg-yellow-100 text-yellow-700",
	generated: "bg-blue-100 text-blue-700",
	finalized: "bg-purple-100 text-purple-700",
	verified: "bg-green-100 text-green-700",
	completed: "bg-green-100 text-green-800",
	cleaned: "bg-gray-100 text-gray-700",
};

export function TestLabPage() {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState("setup");

	const { data: presets } = useQuery({
		queryKey: ["test-lab-presets"],
		queryFn: api.getTestLabPresets,
	});

	const { data: batches, isLoading: batchesLoading } = useQuery({
		queryKey: ["test-lab-batches"],
		queryFn: () => api.getTestLabBatches(),
	});

	const activeBatch = batches?.find(
		(b: TestLabBatch) => b.status !== "cleaned",
	);

	const [organizationId, setOrganizationId] = useState("");
	const [userIdsInput, setUserIdsInput] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [mode, setMode] = useState<"preset" | "custom">("preset");
	const [selectedPreset, setSelectedPreset] = useState("");
	const [productivityPercent, setProductivityPercent] = useState<number | undefined>(undefined);
	const [enableProductiveHours, setEnableProductiveHours] = useState(false);
	const [customDist, setCustomDist] = useState({
		present: 40,
		late: 20,
		absent: 15,
		earlyCheckout: 10,
		leavePending: 15,
	});

	const generateMutation = useMutation({
		mutationFn: () => {
			const userIds = userIdsInput
				.split(",")
				.map((id) => id.trim())
				.filter(Boolean);
			return api.generateTestData({
				organizationId,
				userIds,
				startDate,
				endDate,
				mode,
				presetName: mode === "preset" ? selectedPreset : undefined,
				distribution: mode === "custom" ? customDist : undefined,
				productivityPercent: enableProductiveHours ? productivityPercent : undefined,
			});
		},
		onSuccess: () => {
			toast({ title: "Generated", description: "Test data created successfully" });
			queryClient.invalidateQueries({ queryKey: ["test-lab-batches"] });
			setActiveTab("batch");
		},
		onError: (err: Error) => {
			toast({ title: "Generation Failed", description: err.message, variant: "destructive" });
		},
	});

	const finalizeMutation = useMutation({
		mutationFn: (batchId: string) => api.finalizeTestData(batchId),
		onSuccess: () => {
			toast({ title: "Finalized", description: "Attendance days created" });
			queryClient.invalidateQueries({ queryKey: ["test-lab-batches"] });
		},
		onError: (err: Error) => {
			toast({ title: "Finalization Failed", description: err.message, variant: "destructive" });
		},
	});

	const verifyMutation = useMutation({
		mutationFn: (batchId: string) => api.verifyTestData(batchId),
		onSuccess: () => {
			toast({ title: "Verified", description: "Diff analysis complete" });
			queryClient.invalidateQueries({ queryKey: ["test-lab-batches"] });
			setActiveTab("verification");
		},
		onError: (err: Error) => {
			toast({ title: "Verification Failed", description: err.message, variant: "destructive" });
		},
	});

	const [payrollPreviews, setPayrollPreviews] = useState<TestLabPayrollPreview[]>([]);

	const payrollMutation = useMutation({
		mutationFn: (batchId: string) => api.getPayrollPreview(batchId),
		onSuccess: (data) => {
			setPayrollPreviews(data);
			setActiveTab("payroll");
		},
		onError: (err: Error) => {
			toast({ title: "Payroll Failed", description: err.message, variant: "destructive" });
		},
	});

	const cleanupMutation = useMutation({
		mutationFn: (batchId: string) => api.cleanupTestData(batchId),
		onSuccess: (data) => {
			let desc = `Deleted: ${data.attendanceEntriesDeleted} entries, ${data.attendanceDaysDeleted} days`;
			if (data.trackingSessionsDeleted > 0) desc += `, ${data.trackingSessionsDeleted} sessions`;
			if (data.leaveRequestWarning) desc += `. ${data.leaveRequestWarning}`;
			toast({ title: "Cleaned Up", description: desc });
			queryClient.invalidateQueries({ queryKey: ["test-lab-batches"] });
			setActiveTab("setup");
		},
		onError: (err: Error) => {
			toast({ title: "Cleanup Failed", description: err.message, variant: "destructive" });
		},
	});

	const customDistTotal =
		customDist.present +
		customDist.late +
		customDist.absent +
		customDist.earlyCheckout +
		customDist.leavePending;

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<FlaskConical className="w-6 h-6" />
						Test Lab
					</h1>
					<p className="text-gray-500 mt-1">
						Generate test attendance data, verify calculations, and preview payroll
					</p>
				</div>
				{activeBatch && (
					<Badge className={statusColors[activeBatch.status] || "bg-gray-100"}>
						Active: {activeBatch.status}
					</Badge>
				)}
			</div>

			<Tabs defaultValue="setup" value={activeTab} onValueChange={setActiveTab}>
				<TabsList>
					<TabsTrigger value="setup">Setup</TabsTrigger>
					<TabsTrigger value="batch" disabled={!activeBatch}>
						Active Batch
					</TabsTrigger>
					<TabsTrigger
						value="verification"
						disabled={!activeBatch?.verificationResult}
					>
						Verification
					</TabsTrigger>
					<TabsTrigger
						value="payroll"
						disabled={payrollPreviews.length === 0}
					>
						Payroll Preview
					</TabsTrigger>
					<TabsTrigger value="history">History</TabsTrigger>
				</TabsList>

				{/* SETUP TAB */}
				<TabsContent value="setup" className="space-y-4">
					{activeBatch && (
						<Card className="border-yellow-200 bg-yellow-50">
							<CardContent className="p-4 flex items-center gap-3">
								<AlertTriangle className="w-5 h-5 text-yellow-600" />
								<span className="text-sm text-yellow-800">
									You have an active batch ({activeBatch.batchId.slice(0, 8)}...).
									Clean it up before creating a new one.
								</span>
								<Button
									variant="outline"
									size="sm"
									className="ml-auto"
									onClick={() => setActiveTab("batch")}
								>
									View Batch
								</Button>
							</CardContent>
						</Card>
					)}

					<Card>
						<CardHeader>
							<CardTitle>Organization</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<Label>Organization ID</Label>
							<Input
								placeholder="60f1b2c3d4e5f6a7b8c9d0e1"
								value={organizationId}
								onChange={(e) => setOrganizationId(e.target.value)}
							/>
							<p className="text-xs text-gray-500">
								MongoDB ObjectID of the target organization
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Users className="w-5 h-5" />
								Employees
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<Label>User IDs (comma-separated, max 5)</Label>
							<Input
								placeholder="60f1b2c3d4e5f6a7b8c9d0e1, 60f1b2c3d4e5f6a7b8c9d0e2"
								value={userIdsInput}
								onChange={(e) => setUserIdsInput(e.target.value)}
							/>
							<p className="text-xs text-gray-500">
								Enter MongoDB ObjectIDs of employees from the target organization
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Calendar className="w-5 h-5" />
								Date Range
							</CardTitle>
						</CardHeader>
						<CardContent className="flex gap-4">
							<div className="flex-1">
								<Label>Start Date</Label>
								<Input
									type="date"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
								/>
							</div>
							<div className="flex-1">
								<Label>End Date</Label>
								<Input
									type="date"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
								/>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Distribution Mode</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex gap-2">
								<Button
									variant={mode === "preset" ? "default" : "outline"}
									onClick={() => setMode("preset")}
								>
									Presets
								</Button>
								<Button
									variant={mode === "custom" ? "default" : "outline"}
									onClick={() => setMode("custom")}
								>
									Custom
								</Button>
							</div>

							{mode === "preset" && presets && (
								<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
									{presets.map((p: TestLabPreset) => (
										<div
											key={p.name}
											className={`p-3 border rounded-lg cursor-pointer transition-colors ${
												selectedPreset === p.name
													? "border-blue-500 bg-blue-50"
													: "border-gray-200 hover:border-gray-300"
											}`}
											onClick={() => {
												setSelectedPreset(p.name);
												if (p.distribution.productivityPercent !== undefined) {
													setProductivityPercent(p.distribution.productivityPercent);
													setEnableProductiveHours(true);
												}
											}}
										>
											<p className="font-medium text-sm">{p.label}</p>
											<p className="text-xs text-gray-500 mt-1">{p.description}</p>
											<div className="mt-2 flex flex-wrap gap-1">
												{Object.entries(p.distribution)
													.filter(([key]) => key !== "productivityPercent")
													.map(
														([key, val]) =>
															val > 0 && (
																<span
																	key={key}
																	className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded"
																>
																	{key}: {val}%
																</span>
															),
													)}
											</div>
										</div>
									))}
								</div>
							)}

							{mode === "custom" && (
								<div className="space-y-3">
									{(
										["present", "late", "absent", "earlyCheckout", "leavePending"] as const
									).map((key) => (
										<div key={key} className="flex items-center gap-3">
											<Label className="w-32 capitalize">
												{key === "earlyCheckout"
													? "Early Leave"
													: key === "leavePending"
														? "Leave Pending"
														: key}
											</Label>
											<Input
												type="number"
												min={0}
												max={100}
												value={customDist[key]}
												onChange={(e) =>
													setCustomDist({
														...customDist,
														[key]: Number(e.target.value),
													})
												}
												className="w-20"
											/>
											<span className="text-sm text-gray-500">%</span>
										</div>
									))}
									<p
										className={`text-sm font-medium ${
											customDistTotal === 100
												? "text-green-600"
												: "text-red-600"
										}`}
									>
										Total: {customDistTotal}%{" "}
										{customDistTotal !== 100 && "(must be 100%)"}
									</p>
								</div>
							)}
						</CardContent>
					</Card>

					{/* Productive Hours Simulation */}
					<Card>
						<CardHeader>
							<CardTitle className="text-sm font-medium flex items-center gap-2">
								<Activity className="h-4 w-4" />
								Productive Hours Simulation
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex items-center gap-3">
								<input
									type="checkbox"
									id="enableProductiveHours"
									checked={enableProductiveHours}
									onChange={(e) => {
										setEnableProductiveHours(e.target.checked);
										if (!e.target.checked) setProductivityPercent(undefined);
										if (e.target.checked && productivityPercent === undefined) setProductivityPercent(80);
									}}
									className="h-4 w-4 rounded border-gray-300"
								/>
								<Label htmlFor="enableProductiveHours" className="text-sm">
									Generate desktop tracking sessions
								</Label>
							</div>
							{enableProductiveHours && (
								<div className="space-y-3 pl-7">
									<div className="flex items-center justify-between">
										<Label className="text-sm text-muted-foreground">Productivity Level</Label>
										<span className="text-sm font-mono font-medium">{productivityPercent}%</span>
									</div>
									<input
										type="range"
										min={10}
										max={100}
										step={5}
										value={productivityPercent ?? 80}
										onChange={(e) => setProductivityPercent(Number(e.target.value))}
										className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
									/>
									<div className="flex justify-between text-xs text-muted-foreground">
										<span>10% (Very Low)</span>
										<span>50%</span>
										<span>100% (Full)</span>
									</div>
									<p className="text-xs text-muted-foreground">
										Creates fake TrackingSession records with break/idle/offline times
										calibrated to this productivity level. Used to test productive-hour-based
										payroll proration.
									</p>
								</div>
							)}
						</CardContent>
					</Card>

					<Button
						className="w-full"
						size="lg"
						disabled={
							generateMutation.isPending ||
							!!activeBatch ||
							!userIdsInput ||
							!startDate ||
							!endDate ||
							(mode === "preset" && !selectedPreset) ||
							(mode === "custom" && customDistTotal !== 100)
						}
						onClick={() => generateMutation.mutate()}
					>
						{generateMutation.isPending ? (
							<>
								<RefreshCw className="w-4 h-4 mr-2 animate-spin" />
								Generating...
							</>
						) : (
							<>
								<Play className="w-4 h-4 mr-2" />
								Generate Test Data
							</>
						)}
					</Button>
				</TabsContent>

				{/* ACTIVE BATCH TAB */}
				<TabsContent value="batch" className="space-y-4">
					{activeBatch && (
						<>
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center justify-between">
										<span>Batch: {activeBatch.batchId.slice(0, 8)}...</span>
										<Badge className={statusColors[activeBatch.status]}>
											{activeBatch.status}
										</Badge>
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-3">
									<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
										<div>
											<p className="text-gray-500">Mode</p>
											<p className="font-medium capitalize">
												{activeBatch.config.mode}
												{activeBatch.config.presetName &&
													` (${activeBatch.config.presetName})`}
											</p>
										</div>
										<div>
											<p className="text-gray-500">Date Range</p>
											<p className="font-medium">
												{formatOrgDate(activeBatch.dateRange.startDate)} —{" "}
												{formatOrgDate(activeBatch.dateRange.endDate)}
											</p>
										</div>
										<div>
											<p className="text-gray-500">Employees</p>
											<p className="font-medium">{activeBatch.userIds.length}</p>
										</div>
										<div>
											<p className="text-gray-500">Created</p>
											<p className="font-medium">
												{new Date(activeBatch.createdAt).toLocaleString()}
											</p>
										</div>
										{activeBatch?.config?.productivityPercent !== undefined && (
											<div>
												<p className="text-xs text-muted-foreground">Productivity</p>
												<p className="text-sm font-medium">{activeBatch.config.productivityPercent}%</p>
											</div>
										)}
									</div>

									{activeBatch.userIds.map((u: { _id: string; firstName: string; lastName: string }) => (
										<Badge key={u._id} variant="outline" className="mr-1">
											{u.firstName} {u.lastName}
										</Badge>
									))}
								</CardContent>
							</Card>

							<div className="flex gap-3">
								{activeBatch.status === "generated" && (
									<Button
										onClick={() => finalizeMutation.mutate(activeBatch.batchId)}
										disabled={finalizeMutation.isPending}
									>
										<CheckCircle2 className="w-4 h-4 mr-2" />
										{finalizeMutation.isPending ? "Finalizing..." : "Finalize"}
									</Button>
								)}
								{activeBatch.status === "finalized" && (
									<Button
										onClick={() => verifyMutation.mutate(activeBatch.batchId)}
										disabled={verifyMutation.isPending}
									>
										<ClipboardCheck className="w-4 h-4 mr-2" />
										{verifyMutation.isPending ? "Verifying..." : "Verify"}
									</Button>
								)}
								{["verified", "completed"].includes(activeBatch.status) && (
									<Button
										onClick={() => payrollMutation.mutate(activeBatch.batchId)}
										disabled={payrollMutation.isPending}
									>
										<DollarSign className="w-4 h-4 mr-2" />
										{payrollMutation.isPending ? "Loading..." : "Preview Payroll"}
									</Button>
								)}
								<Button
									variant="destructive"
									onClick={() => cleanupMutation.mutate(activeBatch.batchId)}
									disabled={cleanupMutation.isPending}
								>
									<Trash2 className="w-4 h-4 mr-2" />
									{cleanupMutation.isPending ? "Cleaning..." : "Cleanup"}
								</Button>
							</div>
						</>
					)}
				</TabsContent>

				{/* VERIFICATION TAB */}
				<TabsContent value="verification" className="space-y-4">
					{activeBatch?.verificationResult?.perUser.map(
						(userResult: TestLabUserVerification, idx: number) => (
							<Card key={userResult.userId}>
								<CardHeader>
									<CardTitle className="text-lg">
										User: {activeBatch.userIds[idx]?.firstName}{" "}
										{activeBatch.userIds[idx]?.lastName}
										{userResult.summary.totalMismatches === 0 ? (
											<Badge className="ml-2 bg-green-100 text-green-700">All Match</Badge>
										) : (
											<Badge className="ml-2 bg-red-100 text-red-700">
												{userResult.summary.totalMismatches} Mismatch(es)
											</Badge>
										)}
									</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="grid grid-cols-3 md:grid-cols-6 gap-2">
										{(
											["present", "late", "absent", "leave", "halfDay", "off"] as const
										).map((key) => {
											const data = userResult.summary[key];
											return (
												<div
													key={key}
													className={`p-2 rounded-lg border text-center ${
														data.match
															? "border-green-200 bg-green-50"
															: "border-red-200 bg-red-50"
													}`}
												>
													<p className="text-xs text-gray-500 capitalize">
														{key === "halfDay" ? "Half-Day" : key}
													</p>
													<p className="text-lg font-bold">
														{data.expected}/{data.actual}
													</p>
													<p className="text-[10px] text-gray-400">
														exp / actual
													</p>
												</div>
											);
										})}
									</div>

									<div className="overflow-x-auto">
										<table className="w-full text-sm">
											<thead>
												<tr className="border-b">
													<th className="text-left py-2 px-3 font-medium text-gray-500">Date</th>
													<th className="text-left py-2 px-3 font-medium text-gray-500">Expected</th>
													<th className="text-left py-2 px-3 font-medium text-gray-500">Actual</th>
													<th className="text-center py-2 px-3 font-medium text-gray-500">Match</th>
													<th className="text-left py-2 px-3 font-medium text-gray-500">Details</th>
												</tr>
											</thead>
											<tbody>
												{userResult.dayByDay.map((day, dayIdx) => (
													<tr
														key={dayIdx}
														className={`border-b last:border-0 ${
															day.match
																? ""
																: day.warning
																	? "bg-yellow-50"
																	: "bg-red-50"
														}`}
													>
														<td className="py-2 px-3">
															{formatOrgDate(day.date)}
														</td>
														<td className="py-2 px-3">{day.expected}</td>
														<td className="py-2 px-3">{day.actual}</td>
														<td className="py-2 px-3 text-center">
															{day.match ? "✓" : day.warning ? "⚠" : "✗"}
														</td>
														<td className="py-2 px-3 text-xs text-gray-500">
															{day.warning || day.details || "—"}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</CardContent>
							</Card>
						),
					)}
				</TabsContent>

				{/* PAYROLL PREVIEW TAB */}
				<TabsContent value="payroll" className="space-y-4">
					{payrollPreviews.map((preview, idx) => (
						<Card key={preview.userId}>
							<CardHeader>
								<CardTitle className="text-lg">
									User: {activeBatch?.userIds[idx]?.firstName}{" "}
									{activeBatch?.userIds[idx]?.lastName}
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
									<div className="p-3 bg-blue-50 rounded-lg">
										<p className="text-xs text-gray-500">Working Days</p>
										<p className="text-xl font-bold">{preview.attendanceSummary.totalWorkingDays}</p>
									</div>
									<div className="p-3 bg-green-50 rounded-lg">
										<p className="text-xs text-gray-500">Present</p>
										<p className="text-xl font-bold">{preview.attendanceSummary.totalPresentDays}</p>
									</div>
									<div className="p-3 bg-yellow-50 rounded-lg">
										<p className="text-xs text-gray-500">Late</p>
										<p className="text-xl font-bold">{preview.attendanceSummary.totalLateDays}</p>
									</div>
									<div className="p-3 bg-red-50 rounded-lg">
										<p className="text-xs text-gray-500">Absent</p>
										<p className="text-xl font-bold">{preview.attendanceSummary.totalAbsentDays}</p>
									</div>
								</div>

								<div className="border rounded-lg overflow-hidden">
									<table className="w-full text-sm">
										<tbody>
											<tr className="border-b bg-gray-50">
												<td className="py-2 px-4 font-medium" colSpan={2}>Salary</td>
											</tr>
											<tr className="border-b">
												<td className="py-2 px-4 text-gray-600">Basic</td>
												<td className="py-2 px-4 text-right font-medium">{preview.salary.basicSalary.toLocaleString()}</td>
											</tr>
											<tr className="border-b">
												<td className="py-2 px-4 text-gray-600">Gross</td>
												<td className="py-2 px-4 text-right font-medium">{preview.salary.grossSalary.toLocaleString()}</td>
											</tr>
											<tr className="border-b bg-gray-50">
												<td className="py-2 px-4 font-medium" colSpan={2}>Deductions</td>
											</tr>
											<tr className="border-b">
												<td className="py-2 px-4 text-gray-600">Late Deduction</td>
												<td className="py-2 px-4 text-right text-red-600">-{preview.deductions.lateDeduction.toLocaleString()}</td>
											</tr>
											<tr className="border-b">
												<td className="py-2 px-4 text-gray-600">Absent Deduction</td>
												<td className="py-2 px-4 text-right text-red-600">-{preview.deductions.absentDeduction.toLocaleString()}</td>
											</tr>
											<tr className="border-b">
												<td className="py-2 px-4 text-gray-600">Unpaid Leave</td>
												<td className="py-2 px-4 text-right text-red-600">-{preview.deductions.unpaidLeaveDeduction.toLocaleString()}</td>
											</tr>
											<tr className="border-b">
												<td className="py-2 px-4 text-gray-600">Tax</td>
												<td className="py-2 px-4 text-right text-red-600">-{preview.deductions.tax.toLocaleString()}</td>
											</tr>
											<tr className="border-b">
												<td className="py-2 px-4 text-gray-600">PF</td>
												<td className="py-2 px-4 text-right text-red-600">-{preview.deductions.pf.toLocaleString()}</td>
											</tr>
											<tr className="bg-gray-100">
												<td className="py-3 px-4 font-bold text-lg">Net Pay</td>
												<td className="py-3 px-4 text-right font-bold text-lg text-green-700">{preview.netPay.toLocaleString()}</td>
											</tr>
										</tbody>
									</table>
								</div>

								{preview.productiveHours?.enabled && (
									<div className="mt-4 border-t pt-4">
										<h4 className="text-sm font-medium mb-3 flex items-center gap-2">
											<Activity className="h-4 w-4 text-blue-500" />
											Productive Hours Proration
										</h4>
										<div className="grid grid-cols-2 gap-3">
											<div className="bg-blue-50 rounded-lg p-3">
												<p className="text-xs text-blue-600 font-medium">Source</p>
												<p className="text-sm font-medium capitalize">
													{preview.productiveHours.source.replace(/_/g, " ")}
												</p>
											</div>
											<div className="bg-blue-50 rounded-lg p-3">
												<p className="text-xs text-blue-600 font-medium">Proration</p>
												<p className="text-sm font-medium">
													{preview.productiveHours.prorationPercent}%
												</p>
											</div>
											<div className="bg-gray-50 rounded-lg p-3">
												<p className="text-xs text-muted-foreground">Expected Hours</p>
												<p className="text-sm font-medium">
													{(preview.productiveHours.totalExpectedMinutes / 60).toFixed(1)}h
												</p>
											</div>
											<div className="bg-gray-50 rounded-lg p-3">
												<p className="text-xs text-muted-foreground">Productive Hours</p>
												<p className="text-sm font-medium">
													{(preview.productiveHours.totalProductiveMinutes / 60).toFixed(1)}h
												</p>
											</div>
										</div>
										{preview.productiveHours.prorationPercent < 100 && (
											<div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
												<div className="flex justify-between text-sm">
													<span className="text-amber-700">Salary before proration</span>
													<span className="font-medium">
														{preview.productiveHours.salaryBeforeProration.toLocaleString()}
													</span>
												</div>
												<div className="flex justify-between text-sm mt-1">
													<span className="text-amber-700">
														After proration ({preview.productiveHours.prorationPercent}%)
													</span>
													<span className="font-medium text-amber-800">
														{preview.productiveHours.salaryAfterProration.toLocaleString()}
													</span>
												</div>
												<div className="flex justify-between text-sm mt-1 pt-1 border-t border-amber-200">
													<span className="text-amber-700 font-medium">Reduction</span>
													<span className="font-medium text-red-600">
														-{(preview.productiveHours.salaryBeforeProration - preview.productiveHours.salaryAfterProration).toLocaleString()}
													</span>
												</div>
											</div>
										)}
									</div>
								)}
							</CardContent>
						</Card>
					))}
				</TabsContent>

				{/* HISTORY TAB */}
				<TabsContent value="history" className="space-y-4">
					<Card>
						<CardHeader>
							<CardTitle>Batch History</CardTitle>
						</CardHeader>
						<CardContent>
							{batchesLoading ? (
								<div className="flex justify-center py-8">
									<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
								</div>
							) : !batches?.length ? (
								<p className="text-center py-8 text-gray-500">No batches yet</p>
							) : (
								<div className="overflow-x-auto">
									<table className="w-full text-sm">
										<thead>
											<tr className="border-b">
												<th className="text-left py-2 px-3 font-medium text-gray-500">Batch ID</th>
												<th className="text-left py-2 px-3 font-medium text-gray-500">Date Range</th>
												<th className="text-left py-2 px-3 font-medium text-gray-500">Users</th>
												<th className="text-left py-2 px-3 font-medium text-gray-500">Mode</th>
												<th className="text-left py-2 px-3 font-medium text-gray-500">Status</th>
												<th className="text-left py-2 px-3 font-medium text-gray-500">Created</th>
											</tr>
										</thead>
										<tbody>
											{batches.map((b: TestLabBatch) => (
												<tr key={b.batchId} className="border-b last:border-0 hover:bg-gray-50">
													<td className="py-2 px-3 font-mono text-xs">{b.batchId.slice(0, 8)}...</td>
													<td className="py-2 px-3">
														{formatOrgDate(b.dateRange.startDate)} —{" "}
														{formatOrgDate(b.dateRange.endDate)}
													</td>
													<td className="py-2 px-3">{b.userIds.length}</td>
													<td className="py-2 px-3 capitalize">
														{b.config.mode}
														{b.config.presetName && ` (${b.config.presetName})`}
													</td>
													<td className="py-2 px-3">
														<Badge className={statusColors[b.status] || "bg-gray-100"}>
															{b.status}
														</Badge>
													</td>
													<td className="py-2 px-3">{new Date(b.createdAt).toLocaleString()}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>
		</div>
	);
}
